/**
 * Mode lecture, colonne principale : indices, vidéo de correction, correction.
 */

import React from 'react';
import { Box, Paper, Typography, Divider } from '@mui/material';
import VideoPlayer from '../../common/VideoPlayer';
import { ExerciseFormData } from './types';

interface ReadProps {
  formData: ExerciseFormData;
}

/** Liste des indices (affichée seulement s'il y en a). */
const ExerciseHintsPaper: React.FC<ReadProps> = ({ formData }) => (
  <>
    {formData.hints && formData.hints.length > 0 && (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Indices
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {formData.hints.map((hint, index) => (
          <Box key={index} mb={1}>
            <Typography variant="body2" color="textSecondary">
              Indice {index + 1}:
            </Typography>
            <Typography variant="body1">
              {hint}
            </Typography>
          </Box>
        ))}
      </Paper>
    )}
  </>
);

/** Vidéo de correction et son résumé (affichés si l'un des deux existe). */
const ExerciseVideoPaper: React.FC<ReadProps> = ({ formData }) => (
  <>
    {(formData.url || formData.resume) && (
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Vidéo de correction
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {/* Video Player */}
        {formData.url && (
          <Box mb={formData.resume ? 3 : 0}>
            <VideoPlayer
              youtubeUrl={formData.url}
              title={`Correction - ${formData.title}`}
              aspectRatio="16/9"
            />
          </Box>
        )}

        {/* Resume */}
        {formData.resume && (
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Résumé de la vidéo
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
              {formData.resume}
            </Typography>
          </Box>
        )}
      </Paper>
    )}
  </>
);

/** Correction de l'exercice (toujours affichée). */
const ExerciseCorrectionPaper: React.FC<ReadProps> = ({ formData }) => (
  <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
    <Typography variant="h6" gutterBottom>
      Correction
    </Typography>
    <Divider sx={{ mb: 2 }} />

    {formData.correction ? (
      <Typography
        variant="body1"
        sx={{
          whiteSpace: 'pre-wrap',
          lineHeight: 1.8,
          color: 'text.primary'
        }}
      >
        {formData.correction}
      </Typography>
    ) : (
      <Typography
        variant="body2"
        sx={{
          fontStyle: 'italic',
          color: 'text.secondary'
        }}
      >
        Aucune correction disponible pour cet exercice.
      </Typography>
    )}
  </Paper>
);

/** Contenu principal du mode lecture. */
export const ExerciseReadMain: React.FC<ReadProps> = ({ formData }) => (
  <>
    {/* Hints Section */}
    <ExerciseHintsPaper formData={formData} />

    {/* Video Section with Resume */}
    <ExerciseVideoPaper formData={formData} />

    {/* Correction Section - Toujours affichée */}
    <ExerciseCorrectionPaper formData={formData} />
  </>
);
